import { skipToken } from "@reduxjs/toolkit/query";
import isNil from "lodash/isNil";

import { useGetLastUpdatedUsersQuery } from "@/store/api/tasks";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

interface LastUpdatedSnapshot {
  isCore?: boolean;
  lastUpdatedByUserId?: string;
  lastUpdatedByUserType?: BaseUserType;
}

interface UseAssessmentLastUpdatedProps {
  linkedSurveyId?: string;
  selectedUserId?: string;
  snapshot: LastUpdatedSnapshot;
  tasks: Record<string, TaskItem[]>;
}

interface UseAssessmentLastUpdated extends LastUpdatedSnapshot {
  isLookupFailed: boolean;
  isLookupPending: boolean;
}

interface FindAssigneeTypeOptions {
  selectedUserId?: string;
  tasks: Record<string, TaskItem[]>;
  userId?: string;
}

const findAssigneeType = ({
  selectedUserId,
  tasks,
  userId,
}: FindAssigneeTypeOptions): BaseUserType | undefined => {
  if (isNil(selectedUserId) || isNil(tasks[selectedUserId])) {
    return undefined;
  }
  return tasks[selectedUserId]
    .flatMap((task: TaskItem) => task.familyAssigneeUsers ?? [])
    .find((user: FamilyAssigneeUser) => user.externalId === userId)?.type;
};

const useAssessmentLastUpdated = ({
  linkedSurveyId,
  selectedUserId,
  snapshot,
  tasks,
}: UseAssessmentLastUpdatedProps): UseAssessmentLastUpdated => {
  const { currentData: lastUpdatedUsers, isError: isLookupFailed } =
    useGetLastUpdatedUsersQuery(
      isNotNilNotEmpty(linkedSurveyId) ? [linkedSurveyId] : skipToken,
    );
  const isLookupPending =
    isNotNilNotEmpty(linkedSurveyId) &&
    isNil(lastUpdatedUsers) &&
    !isLookupFailed;
  const liveLastUpdated = lastUpdatedUsers?.find(
    (info: LastUpdatedUserResponse) =>
      info.responseExternalId === linkedSurveyId,
  );

  if (!liveLastUpdated) {
    return { ...snapshot, isLookupFailed, isLookupPending };
  }

  const isSameUserAsSnapshot =
    liveLastUpdated.lastUpdatedByUserId === snapshot.lastUpdatedByUserId;

  return {
    isCore: liveLastUpdated.isCore,
    isLookupFailed,
    isLookupPending,
    lastUpdatedByUserId: liveLastUpdated.lastUpdatedByUserId,
    lastUpdatedByUserType:
      findAssigneeType({
        selectedUserId,
        tasks,
        userId: liveLastUpdated.lastUpdatedByUserId,
      }) ?? (isSameUserAsSnapshot ? snapshot.lastUpdatedByUserType : undefined),
  };
};

export default useAssessmentLastUpdated;
